/*
 * Hesatek header + mega menu v2
 * Local preview asset. Shopify/Warehouse remains the owner of predictive search.
 */

(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-hh-root]';
  const DESKTOP_QUERY = '(min-width: 1024px)';
  const FINE_POINTER_QUERY = '(hover: hover) and (pointer: fine)';
  const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'summary',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  const desktopMedia = window.matchMedia(DESKTOP_QUERY);
  const finePointerMedia = window.matchMedia(FINE_POINTER_QUERY);
  const reducedMotionMedia = window.matchMedia(REDUCED_MOTION_QUERY);
  const initializedRoots = new WeakSet();
  let generatedId = 0;
  let pageLockCount = 0;
  let savedPageStyles = null;

  function addMediaListener(media, callback) {
    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', callback);
      return () => media.removeEventListener('change', callback);
    }

    media.addListener(callback);
    return () => media.removeListener(callback);
  }

  function ensureId(element, prefix) {
    if (element.id) return element.id;
    generatedId += 1;
    element.id = `${prefix}-${generatedId}`;
    return element.id;
  }

  function getVisibleFocusables(container) {
    return [...container.querySelectorAll(FOCUSABLE_SELECTOR)].filter((element) => {
      if (element.closest('[hidden], [inert], [aria-hidden="true"]')) return false;
      if (element.getAttribute('aria-hidden') === 'true') return false;
      if (element.tabIndex < 0) return false;
      return element.getClientRects().length > 0;
    });
  }

  function focusWithoutScroll(element) {
    if (!element || !element.isConnected) return;

    try {
      element.focus({ preventScroll: true });
    } catch (_error) {
      element.focus();
    }
  }

  function lockPageScroll() {
    pageLockCount += 1;
    if (pageLockCount !== 1) return;

    const html = document.documentElement;
    const body = document.body;
    const scrollbarWidth = Math.max(0, window.innerWidth - html.clientWidth);
    const computedBodyPadding = Number.parseFloat(
      window.getComputedStyle(body).paddingRight,
    ) || 0;

    savedPageStyles = {
      htmlOverflow: html.style.overflow,
      htmlOverscrollBehavior: html.style.overscrollBehavior,
      bodyOverflow: body.style.overflow,
      bodyOverscrollBehavior: body.style.overscrollBehavior,
      bodyPaddingRight: body.style.paddingRight,
    };

    html.style.overflow = 'hidden';
    html.style.overscrollBehavior = 'none';
    body.style.overflow = 'hidden';
    body.style.overscrollBehavior = 'none';

    if (scrollbarWidth > 0) {
      body.style.paddingRight = `${computedBodyPadding + scrollbarWidth}px`;
    }
  }

  function unlockPageScroll() {
    if (pageLockCount === 0) return;
    pageLockCount -= 1;
    if (pageLockCount !== 0 || !savedPageStyles) return;

    const html = document.documentElement;
    const body = document.body;

    html.style.overflow = savedPageStyles.htmlOverflow;
    html.style.overscrollBehavior = savedPageStyles.htmlOverscrollBehavior;
    body.style.overflow = savedPageStyles.bodyOverflow;
    body.style.overscrollBehavior = savedPageStyles.bodyOverscrollBehavior;
    body.style.paddingRight = savedPageStyles.bodyPaddingRight;
    savedPageStyles = null;
  }

  function initRoot(root) {
    if (!(root instanceof HTMLElement) || initializedRoots.has(root)) return;
    initializedRoots.add(root);
    root.classList.add('hh-is-ready');

    let sectionSettings = {};
    try {
      sectionSettings = JSON.parse(root.dataset.sectionSettings || '{}');
    } catch (_error) {
      sectionSettings = {};
    }
    const allowDesktopHover = sectionSettings.desktopOpenTrigger !== 'click';

    const drawer = root.querySelector('[data-hh-drawer]');
    const drawerToggles = [...root.querySelectorAll('[data-hh-drawer-toggle]')];
    const drawerCloseButtons = [
      ...root.querySelectorAll('[data-hh-drawer-close]'),
    ];
    const drawerBackButton = root.querySelector('[data-hh-drawer-back]');
    const drawerTitle = root.querySelector('[data-hh-drawer-title]');
    const searchToggles = [...root.querySelectorAll('[data-hh-search-toggle]')];
    const searchWrappers = [
      ...root.querySelectorAll('.header__search-bar-wrapper'),
    ];
    const allTriggers = [...root.querySelectorAll('[data-hh-trigger]')];
    const allPanels = [...root.querySelectorAll('[data-hh-panel]')];
    const panelByTrigger = new Map();
    const cleanupCallbacks = [];
    let openDesktopTrigger = null;
    let openDesktopPanel = null;
    let desktopOpenMode = null;
    let drawerOpen = false;
    let drawerReturnFocus = null;
    let drawerHideTimer = 0;
    let drawerPanelHideTimer = 0;
    let pendingDrawerPanelsToHide = [];
    let openDrawerTrigger = null;
    let openDrawerPanel = null;
    let drawerPanelStack = [];
    let backdropHideTimer = 0;
    let hoverOpenTimer = 0;
    let hoverCloseTimer = 0;
    let backdrop = root.querySelector('[data-hh-backdrop]');
    if (drawer && !backdrop) {
      backdrop = document.createElement('div');
      backdrop.className = 'hh-drawer-backdrop';
      backdrop.setAttribute('data-hh-backdrop', '');
      backdrop.setAttribute('aria-hidden', 'true');
      backdrop.hidden = true;
      root.append(backdrop);
    }

    if (backdrop) {
      backdrop.classList.add('hh-drawer-backdrop');
      backdrop.classList.remove('hh-is-open');
      backdrop.setAttribute('aria-hidden', 'true');
      backdrop.hidden = true;
    }

    function panelForTrigger(trigger) {
      if (panelByTrigger.has(trigger)) return panelByTrigger.get(trigger);

      const controlledId = trigger.getAttribute('aria-controls');
      if (controlledId) {
        const controlledPanel = document.getElementById(controlledId);
        if (
          controlledPanel &&
          root.contains(controlledPanel) &&
          controlledPanel.matches('[data-hh-panel]')
        ) {
          panelByTrigger.set(trigger, controlledPanel);
          return controlledPanel;
        }
      }

      const token = trigger.getAttribute('data-hh-trigger');
      const triggerIsInDrawer = Boolean(drawer && drawer.contains(trigger));
      const matchingPanel = allPanels.find((panel) => {
        if (panel.getAttribute('data-hh-panel') !== token) return false;
        return Boolean(drawer && drawer.contains(panel)) === triggerIsInDrawer;
      });

      panelByTrigger.set(trigger, matchingPanel || null);
      return matchingPanel || null;
    }

    function prepareTrigger(trigger) {
      const panel = panelForTrigger(trigger);
      if (!panel) return;

      const panelId = ensureId(panel, 'hh-panel');
      trigger.setAttribute('aria-controls', panelId);
      trigger.setAttribute('aria-expanded', 'false');
      panel.setAttribute('aria-hidden', 'true');
      panel.classList.remove('hh-is-open');
      panel.hidden = true;
    }

    allTriggers.forEach(prepareTrigger);

    function cancelHoverTimers() {
      window.clearTimeout(hoverOpenTimer);
      window.clearTimeout(hoverCloseTimer);
      hoverOpenTimer = 0;
      hoverCloseTimer = 0;
    }

    function dispatchStateEvent(name, detail) {
      root.dispatchEvent(
        new CustomEvent(name, {
          bubbles: true,
          detail,
        }),
      );
    }

    function closeDesktopPanel({ restoreFocus = false } = {}) {
      cancelHoverTimers();
      if (!openDesktopPanel || !openDesktopTrigger) return;

      const triggerToRestore = openDesktopTrigger;
      openDesktopTrigger.setAttribute('aria-expanded', 'false');
      openDesktopPanel.classList.remove('hh-is-open');
      openDesktopPanel.setAttribute('aria-hidden', 'true');
      openDesktopPanel.hidden = true;
      root.classList.remove('hh-is-mega-open');

      dispatchStateEvent('hh:panel-close', {
        trigger: openDesktopTrigger,
        panel: openDesktopPanel,
      });

      openDesktopTrigger = null;
      openDesktopPanel = null;
      desktopOpenMode = null;

      if (restoreFocus) focusWithoutScroll(triggerToRestore);
    }

    function openDesktopMenu(trigger, panel, mode = 'click') {
      if (!desktopMedia.matches || !panel) return;
      if (openDesktopPanel === panel) {
        desktopOpenMode = mode;
        return;
      }

      closeDesktopPanel();
      openDesktopTrigger = trigger;
      openDesktopPanel = panel;
      desktopOpenMode = mode;
      trigger.setAttribute('aria-expanded', 'true');
      panel.hidden = false;
      panel.setAttribute('aria-hidden', 'false');
      panel.classList.remove('hh-is-open');
      void panel.offsetWidth;
      panel.classList.add('hh-is-open');
      root.classList.add('hh-is-mega-open');

      dispatchStateEvent('hh:panel-open', { trigger, panel, mode });
    }

    function setDrawerRootFocusable(isFocusable) {
      if (!drawer) return;

      drawer
        .querySelectorAll(
          ':scope > .hh-drawer__body > .hh-drawer__search, :scope > .hh-drawer__body > .hh-drawer__nav > .hh-drawer__group > .hh-drawer__trigger',
        )
        .forEach((element) => {
          if (!(element instanceof HTMLElement)) return;
          element.inert = !isFocusable;
          if (isFocusable) element.removeAttribute('tabindex');
          else element.setAttribute('tabindex', '-1');
        });

      drawer.querySelectorAll(':scope > .hh-drawer__body > .hh-drawer__actions, :scope > .hh-drawer__body > .hh-drawer__localization').forEach((element) => {
        if (!(element instanceof HTMLElement)) return;
        element.inert = !isFocusable;
      });
    }

    function setDrawerTitle(value) {
      if (!drawerTitle) return;
      const rootTitle = drawerTitle.getAttribute('data-hh-root-title') || 'Valikko';
      drawerTitle.textContent = value || rootTitle;
    }

    function setDrawerBackLabel(parentTitle = '') {
      if (!drawerBackButton) return;

      const normalizedTitle = parentTitle.trim().toLocaleLowerCase('fi');
      const destinationByTitle = {
        verkkokauppa: 'Verkkokauppaan',
        palvelut: 'Palveluihin',
        'meistä': 'Meistä-näkymään',
      };
      const destination = destinationByTitle[normalizedTitle];
      drawerBackButton.setAttribute(
        'aria-label',
        destination
          ? `Palaa ${destination}`
          : parentTitle
            ? `Palaa ${parentTitle}-näkymään`
            : 'Palaa päävalikkoon',
      );
    }

    function finishPendingDrawerPanelHide() {
      window.clearTimeout(drawerPanelHideTimer);
      drawerPanelHideTimer = 0;
      pendingDrawerPanelsToHide.forEach((panel) => {
        if (panel instanceof HTMLElement && panel !== openDrawerPanel) {
          panel.hidden = true;
        }
      });
      pendingDrawerPanelsToHide = [];
    }

    function scheduleDrawerPanelsHide(panels, delay) {
      finishPendingDrawerPanelHide();
      pendingDrawerPanelsToHide = [
        ...new Set(panels.filter((panel) => panel instanceof HTMLElement)),
      ];

      if (delay === 0 || reducedMotionMedia.matches) {
        finishPendingDrawerPanelHide();
        return;
      }

      drawerPanelHideTimer = window.setTimeout(
        finishPendingDrawerPanelHide,
        delay,
      );
    }

    function closeDrawerPanel({ restoreFocus = false, immediate = false } = {}) {
      finishPendingDrawerPanelHide();
      const triggerToRestore =
        drawerPanelStack[0]?.trigger || openDrawerTrigger;
      const frames = [
        ...drawerPanelStack,
        ...(openDrawerPanel && openDrawerTrigger
          ? [{ trigger: openDrawerTrigger, panel: openDrawerPanel }]
          : []),
      ];

      frames.forEach(({ trigger, panel }) => {
        trigger?.setAttribute('aria-expanded', 'false');
        panel?.classList.remove('hh-is-open', 'hh-is-parent');
        panel?.setAttribute('aria-hidden', 'true');
        if (panel instanceof HTMLElement) panel.inert = true;
      });

      openDrawerTrigger = null;
      openDrawerPanel = null;
      drawerPanelStack = [];
      drawer?.classList.remove('hh-is-drilled');
      if (drawerBackButton) drawerBackButton.hidden = true;
      setDrawerTitle();
      setDrawerBackLabel();
      setDrawerRootFocusable(true);

      scheduleDrawerPanelsHide(
        frames.map(({ panel }) => panel),
        immediate ? 0 : 200,
      );

      if (restoreFocus) focusWithoutScroll(triggerToRestore);
    }

    function openDrawerDrilldown(trigger, panel) {
      if (!drawer || !drawerOpen || !trigger || !panel) return;
      if (openDrawerPanel === panel) return;
      finishPendingDrawerPanelHide();

      if (openDrawerPanel && openDrawerTrigger) {
        openDrawerPanel.classList.remove('hh-is-open');
        openDrawerPanel.classList.add('hh-is-parent');
        openDrawerPanel.setAttribute('aria-hidden', 'true');
        openDrawerPanel.inert = true;
        drawerPanelStack.push({
          trigger: openDrawerTrigger,
          panel: openDrawerPanel,
          title: drawerTitle?.textContent?.trim() || '',
        });
      }

      openDrawerTrigger = trigger;
      openDrawerPanel = panel;
      trigger.setAttribute('aria-expanded', 'true');
      panel.hidden = false;
      panel.inert = false;
      panel.setAttribute('aria-hidden', 'false');
      setDrawerRootFocusable(false);
      drawer.classList.add('hh-is-drilled');
      if (drawerBackButton) drawerBackButton.hidden = false;
      const label = trigger.querySelector('[data-hh-drawer-label]')?.textContent?.trim();
      setDrawerTitle(label);
      setDrawerBackLabel(drawerPanelStack.at(-1)?.title || '');

      void panel.offsetWidth;
      panel.classList.add('hh-is-open');
      focusWithoutScroll(drawerBackButton || getVisibleFocusables(panel)[0] || panel);
    }

    function goBackDrawerPanel() {
      if (!openDrawerPanel || !openDrawerTrigger) return;
      if (drawerPanelStack.length === 0) {
        closeDrawerPanel({ restoreFocus: true });
        return;
      }

      finishPendingDrawerPanelHide();
      const triggerToRestore = openDrawerTrigger;
      const panelToHide = openDrawerPanel;
      openDrawerTrigger.setAttribute('aria-expanded', 'false');
      openDrawerPanel.classList.remove('hh-is-open');
      openDrawerPanel.setAttribute('aria-hidden', 'true');
      openDrawerPanel.inert = true;

      const previous = drawerPanelStack.pop();
      openDrawerTrigger = previous.trigger;
      openDrawerPanel = previous.panel;
      openDrawerTrigger.setAttribute('aria-expanded', 'true');
      openDrawerPanel.hidden = false;
      openDrawerPanel.inert = false;
      openDrawerPanel.classList.remove('hh-is-parent');
      openDrawerPanel.setAttribute('aria-hidden', 'false');
      void openDrawerPanel.offsetWidth;
      openDrawerPanel.classList.add('hh-is-open');
      setDrawerTitle(previous.title);
      setDrawerBackLabel(drawerPanelStack.at(-1)?.title || '');
      setDrawerRootFocusable(false);
      drawer.classList.add('hh-is-drilled');
      if (drawerBackButton) drawerBackButton.hidden = false;

      scheduleDrawerPanelsHide([panelToHide], 220);

      focusWithoutScroll(triggerToRestore);
    }

    function closeMobileSearch() {
      if (desktopMedia.matches) return;

      searchWrappers.forEach((wrapper) => {
        if (!wrapper.classList.contains('is-visible')) return;
        const nativeClose = wrapper.querySelector('[data-action="unfix-search"]');
        if (nativeClose instanceof HTMLElement) {
          nativeClose.click();
        } else {
          wrapper.classList.remove('is-visible');
          wrapper.setAttribute('aria-hidden', 'true');
        }
      });
    }

    function syncSearchState() {
      const isVisible = searchWrappers.some((wrapper) =>
        wrapper.classList.contains('is-visible'),
      );
      searchToggles.forEach((toggle) => {
        toggle.setAttribute('aria-expanded', String(isVisible));
      });
    }

    function focusSearchWhenReady() {
      window.setTimeout(() => {
        const visibleWrapper = searchWrappers.find(
          (wrapper) =>
            wrapper.classList.contains('is-visible') || desktopMedia.matches,
        );
        const input = visibleWrapper?.querySelector('.search-bar__input');
        if (input instanceof HTMLElement) focusWithoutScroll(input);
        syncSearchState();
      }, reducedMotionMedia.matches ? 0 : 32);
    }

    function showBackdrop() {
      if (!backdrop) return;
      window.clearTimeout(backdropHideTimer);
      backdrop.hidden = false;
      backdrop.setAttribute('aria-hidden', 'false');
      void backdrop.offsetWidth;
      backdrop.classList.add('hh-is-open');
    }

    function hideBackdrop() {
      if (!backdrop) return;
      backdrop.classList.remove('hh-is-open');
      backdrop.setAttribute('aria-hidden', 'true');
      window.clearTimeout(backdropHideTimer);
      backdropHideTimer = window.setTimeout(
        () => {
          if (!drawerOpen) backdrop.hidden = true;
        },
        reducedMotionMedia.matches ? 0 : 200,
      );
    }

    function openDrawer(returnFocusElement) {
      if (!drawer || desktopMedia.matches || drawerOpen) return;
      closeDesktopPanel();
      closeMobileSearch();
      closeDrawerPanel({ immediate: true });
      window.clearTimeout(drawerHideTimer);
      drawerOpen = true;
      drawerReturnFocus = returnFocusElement || document.activeElement;
      drawer.hidden = false;
      drawer.setAttribute('aria-hidden', 'false');
      root.classList.add('hh-is-drawer-open');
      drawerToggles.forEach((toggle) =>
        toggle.setAttribute('aria-expanded', 'true'),
      );
      showBackdrop();
      lockPageScroll();

      void drawer.offsetWidth;
      drawer.classList.add('hh-is-open');
      const preferredFocus =
        drawer.querySelector('[data-hh-drawer-close]') ||
        getVisibleFocusables(drawer)[0] ||
        drawer;
      focusWithoutScroll(preferredFocus);

      dispatchStateEvent('hh:drawer-open', { drawer });
    }

    function closeDrawer({ restoreFocus = true } = {}) {
      if (!drawer || !drawerOpen) return;
      const returnFocusElement = drawerReturnFocus;
      drawerOpen = false;
      closeDrawerPanel({ immediate: true });
      drawer.classList.remove('hh-is-open');
      drawer.setAttribute('aria-hidden', 'true');
      root.classList.remove('hh-is-drawer-open');
      drawerToggles.forEach((toggle) =>
        toggle.setAttribute('aria-expanded', 'false'),
      );
      hideBackdrop();
      unlockPageScroll();
      window.clearTimeout(drawerHideTimer);
      drawerHideTimer = window.setTimeout(
        () => {
          if (!drawerOpen) drawer.hidden = true;
        },
        reducedMotionMedia.matches ? 0 : 200,
      );

      drawerReturnFocus = null;
      if (restoreFocus) focusWithoutScroll(returnFocusElement);
      dispatchStateEvent('hh:drawer-close', { drawer });
    }

    if (drawer) {
      ensureId(drawer, 'hh-drawer');
      drawer.hidden = true;
      drawer.setAttribute('aria-hidden', 'true');
      drawer.setAttribute('aria-modal', 'true');
      if (!drawer.hasAttribute('role')) drawer.setAttribute('role', 'dialog');
      if (!drawer.hasAttribute('aria-label')) drawer.setAttribute('aria-label', 'Valikko');
      if (!drawer.hasAttribute('tabindex')) drawer.setAttribute('tabindex', '-1');

      drawerToggles.forEach((toggle) => {
        toggle.setAttribute('aria-controls', drawer.id);
        toggle.setAttribute('aria-expanded', 'false');
      });
    }

    function handleRootClick(event) {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const backdropTarget = target.closest('[data-hh-backdrop]');
      if (backdropTarget && root.contains(backdropTarget)) {
        event.preventDefault();
        closeDrawer();
        return;
      }

      const drawerToggle = target.closest('[data-hh-drawer-toggle]');
      if (drawerToggle && root.contains(drawerToggle)) {
        event.preventDefault();
        if (drawerOpen) closeDrawer();
        else openDrawer(drawerToggle);
        return;
      }

      const drawerClose = target.closest('[data-hh-drawer-close]');
      if (drawerClose && root.contains(drawerClose)) {
        event.preventDefault();
        closeDrawer();
        return;
      }

      const drawerBack = target.closest('[data-hh-drawer-back]');
      if (drawerBack && root.contains(drawerBack)) {
        event.preventDefault();
        goBackDrawerPanel();
        return;
      }

      const searchToggle = target.closest('[data-hh-search-toggle]');
      if (searchToggle && root.contains(searchToggle)) {
        closeDesktopPanel();
        if (drawerOpen) closeDrawer({ restoreFocus: false });

        const hasWarehouseAction =
          searchToggle.getAttribute('data-action') === 'toggle-search';
        const searchWrapper = searchWrappers[0];
        if (!hasWarehouseAction && searchWrapper) {
          event.preventDefault();
          searchWrapper.hidden = false;
          searchWrapper.classList.add('is-visible');
          searchWrapper.setAttribute('aria-hidden', 'false');
        }

        focusSearchWhenReady();
        return;
      }

      const trigger = target.closest('[data-hh-trigger]');
      if (trigger && root.contains(trigger)) {
        const panel = panelForTrigger(trigger);
        if (!panel) return;
        event.preventDefault();

        if (drawer && drawer.contains(trigger)) {
          openDrawerDrilldown(trigger, panel);
          return;
        }

        if (!desktopMedia.matches) return;
        if (openDesktopPanel === panel && desktopOpenMode === 'click') {
          closeDesktopPanel();
        } else {
          openDesktopMenu(trigger, panel, 'click');
        }
        return;
      }

      const panelLink = target.closest('[data-hh-panel] a[href]');
      if (panelLink && root.contains(panelLink)) {
        if (drawer && drawer.contains(panelLink)) {
          closeDrawer({ restoreFocus: false });
        } else {
          closeDesktopPanel();
        }
        return;
      }

      const otherHeaderLink = target.closest('a[href]');
      if (otherHeaderLink && root.contains(otherHeaderLink)) {
        closeDesktopPanel();
      }
    }

    function handleRootKeydown(event) {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const trigger = target.closest('[data-hh-trigger]');
      if (!trigger || !root.contains(trigger) || (drawer && drawer.contains(trigger))) {
        return;
      }

      const panel = panelForTrigger(trigger);
      if (!panel || !desktopMedia.matches) return;
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

      event.preventDefault();
      openDesktopMenu(trigger, panel, 'keyboard');
      const panelFocusables = getVisibleFocusables(panel);
      const nextFocus =
        event.key === 'ArrowUp'
          ? panelFocusables[panelFocusables.length - 1]
          : panelFocusables[0];
      focusWithoutScroll(nextFocus);
    }

    function handleDocumentPointerDown(event) {
      if (openDesktopPanel && !root.contains(event.target)) closeDesktopPanel();
    }

    function handleDocumentFocusIn(event) {
      const target = event.target;
      if (!(target instanceof Element) || !openDesktopPanel) return;
      if (!root.contains(target)) {
        closeDesktopPanel();
        return;
      }

      if (target.closest('.header__search-bar-wrapper')) {
        closeDesktopPanel();
        return;
      }

      const focusedTrigger = target.closest('[data-hh-trigger]');
      if (
        focusedTrigger &&
        focusedTrigger !== openDesktopTrigger &&
        !(drawer && drawer.contains(focusedTrigger))
      ) {
        closeDesktopPanel();
      }
    }

    function handleDocumentKeydown(event) {
      if (event.key === 'Escape') {
        const localeTrigger = root.querySelector('.hh-action--locale [data-action="toggle-popover"][aria-expanded="true"]');
        if (localeTrigger) {
          event.preventDefault();
          localeTrigger.click();
          focusWithoutScroll(localeTrigger);
          return;
        }

        if (drawerOpen) {
          event.preventDefault();
          closeDrawer();
          return;
        }

        if (openDesktopPanel) {
          event.preventDefault();
          closeDesktopPanel({ restoreFocus: true });
        }
        return;
      }

      if (event.key !== 'Tab' || !drawerOpen || !drawer) return;
      const focusables = getVisibleFocusables(drawer);
      if (focusables.length === 0) {
        event.preventDefault();
        focusWithoutScroll(drawer);
        return;
      }

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && (active === first || !drawer.contains(active))) {
        event.preventDefault();
        focusWithoutScroll(last);
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        focusWithoutScroll(first);
      }
    }

    function handleShopifyBlockSelect(event) {
      if (!window.Shopify?.designMode || !desktopMedia.matches) return;
      const selectedElement = event.target;
      if (!(selectedElement instanceof Element) || !root.contains(selectedElement)) {
        return;
      }

      const panel = selectedElement.closest('[data-hh-panel]');
      if (!panel || (drawer && drawer.contains(panel))) return;
      const trigger = allTriggers.find(
        (candidate) =>
          !(drawer && drawer.contains(candidate)) && panelForTrigger(candidate) === panel,
      );
      if (trigger) openDesktopMenu(trigger, panel, 'editor');
    }

    function scheduleHoverOpen(trigger, panel) {
      if (!allowDesktopHover || !finePointerMedia.matches || !desktopMedia.matches) {
        return;
      }
      window.clearTimeout(hoverCloseTimer);
      window.clearTimeout(hoverOpenTimer);
      hoverOpenTimer = window.setTimeout(
        () => openDesktopMenu(trigger, panel, 'hover'),
        120,
      );
    }

    function scheduleHoverClose() {
      if (!allowDesktopHover) return;
      window.clearTimeout(hoverOpenTimer);
      hoverOpenTimer = 0;
      if (desktopOpenMode !== 'hover') return;
      window.clearTimeout(hoverCloseTimer);
      hoverCloseTimer = window.setTimeout(() => closeDesktopPanel(), 200);
    }

    allTriggers.forEach((trigger) => {
      if (drawer && drawer.contains(trigger)) return;
      const panel = panelForTrigger(trigger);
      if (!panel) return;

      const onTriggerEnter = () => scheduleHoverOpen(trigger, panel);
      const onTriggerLeave = (event) => {
        if (panel.contains(event.relatedTarget)) return;
        scheduleHoverClose();
      };
      const onPanelEnter = () => {
        window.clearTimeout(hoverCloseTimer);
      };
      const onPanelLeave = (event) => {
        if (trigger.contains(event.relatedTarget)) return;
        scheduleHoverClose();
      };

      trigger.addEventListener('pointerenter', onTriggerEnter);
      trigger.addEventListener('pointerleave', onTriggerLeave);
      panel.addEventListener('pointerenter', onPanelEnter);
      panel.addEventListener('pointerleave', onPanelLeave);
      cleanupCallbacks.push(() => {
        trigger.removeEventListener('pointerenter', onTriggerEnter);
        trigger.removeEventListener('pointerleave', onTriggerLeave);
        panel.removeEventListener('pointerenter', onPanelEnter);
        panel.removeEventListener('pointerleave', onPanelLeave);
      });
    });

    const searchObservers = searchWrappers.map((wrapper) => {
      const observer = new MutationObserver(() => {
        syncSearchState();
        if (wrapper.classList.contains('is-visible')) closeDesktopPanel();
      });
      observer.observe(wrapper, { attributes: true, attributeFilter: ['class'] });
      return observer;
    });

    const handleBreakpointChange = () => {
      closeDesktopPanel();
      if (desktopMedia.matches) closeDrawer({ restoreFocus: false });
      syncSearchState();
    };

    const removeBreakpointListener = addMediaListener(
      desktopMedia,
      handleBreakpointChange,
    );

    root.addEventListener('click', handleRootClick);
    root.addEventListener('keydown', handleRootKeydown);
    document.addEventListener('pointerdown', handleDocumentPointerDown);
    document.addEventListener('focusin', handleDocumentFocusIn);
    document.addEventListener('keydown', handleDocumentKeydown);
    document.addEventListener('shopify:block:select', handleShopifyBlockSelect);
    syncSearchState();

    cleanupCallbacks.push(() => {
      root.removeEventListener('click', handleRootClick);
      root.removeEventListener('keydown', handleRootKeydown);
      document.removeEventListener('pointerdown', handleDocumentPointerDown);
      document.removeEventListener('focusin', handleDocumentFocusIn);
      document.removeEventListener('keydown', handleDocumentKeydown);
      document.removeEventListener('shopify:block:select', handleShopifyBlockSelect);
      removeBreakpointListener();
      searchObservers.forEach((observer) => observer.disconnect());
      closeDesktopPanel();
      closeDrawer({ restoreFocus: false });
    });

    root.__hhDestroy = () => {
      cleanupCallbacks.forEach((cleanup) => cleanup());
      window.clearTimeout(drawerHideTimer);
      window.clearTimeout(backdropHideTimer);
      finishPendingDrawerPanelHide();
      cancelHoverTimers();
      initializedRoots.delete(root);
      delete root.__hhDestroy;
    };
  }

  function initWithin(scope = document) {
    if (scope.matches?.(ROOT_SELECTOR)) initRoot(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(initRoot);
  }

  function init() {
    initWithin(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initWithin(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.matches?.(ROOT_SELECTOR)
      ? event.target
      : event.target.querySelector?.(ROOT_SELECTOR);
    root?.__hhDestroy?.();
  });
})();
